import { PanelBase } from "./PanelBase";
import type { GameContext } from "../../GameContext";

interface BaseStats {
  str: number;
  agi: number;
  vit: number;
  int: number;
  dex: number;
  luk: number;
}

interface DerivedStats {
  maxHp: number;
  maxMp: number;
  weaponAtk: number;
  weaponMatk: number;
  def: number;
  matDef: number;
  attackSpeed: number;
}

/**
 * Matches player-worker's actual CharacterDetail response exactly (see
 * its types.ts + mapper.ts toDetail()) — this previously declared a flat
 * shape (job/jobLevel/hp/maxHp/mp/maxMp/weaponAtk/weaponMatk/def/matDef/
 * attackSpeedMod) that the server has never actually sent. The real
 * response nests everything under baseStats/derivedStats, uses jobId (not
 * job) + jobTier (not jobLevel), and has no "current HP/MP" field at all
 * — there's no persistent current-HP tracking on the character row
 * outside of combat's own ephemeral per-encounter state (see
 * combat-worker's monsterLookup.ts), so this only ever had Max HP/MP to
 * show, never a real current/max pair.
 *
 * BUG THIS FIXES: stats.job was always undefined against the real
 * response, and `stats.job.toLowerCase()` below threw synchronously
 * inside render() — a TypeError with nothing to catch it, so the DOM
 * stayed on refresh()'s initial "Loading character sheet…" text forever
 * for every character. If this looked like it "worked" for exactly one
 * session, that's far more likely a stale cached client bundle in that
 * one browser tab than anything about that specific character's data —
 * this bug had nothing to do with which character or account was used.
 */
interface CharacterStats {
  name: string;
  jobId: string;
  jobTier: number;
  baseLevel: number;
  totalXp: number;
  skillPoints: number;
  baseStats: BaseStats;
  derivedStats: DerivedStats;
}

/**
 * Maps a jobId to a processed portrait illustration (art/portraits/), for
 * the 4 Tier-1 classes this project actually has character art for (from
 * the second "More Graphics" delivery). Deliberately no entry for
 * "wanderer" (every character starts here, before any job-change) or any
 * Tier-2/3 class — there's no art for those yet, and this shouldn't
 * pretend otherwise. See art/MORE_GRAPHICS_STATUS.md for the full
 * picture of what is/isn't covered.
 */
const PORTRAIT_BY_JOB: Record<string, string> = {
  blademaster: "/art/portraits/portrait-blademaster.png",
  ranger: "/art/portraits/portrait-ranger.png",
  arcanist: "/art/portraits/portrait-arcanist.png",
  sentinel: "/art/portraits/portrait-sentinel.png",
};

/**
 * Per 1.4: class balance numbers (hpGrowth/mpGrowth, WeaponAtk/WeaponMatk
 * vs def/matDef, attackSpeedMod) live server-side in
 * packages/shared-game/StatCalculator.ts as the single source of truth --
 * "no worker computes derived stats independently." This panel only ever
 * DISPLAYS what GET /api/v1/characters/:id returns; it performs no stat
 * math of its own.
 */
export class CharacterSheet extends PanelBase {
  constructor(root: HTMLElement, ctx: GameContext, private readonly characterId: string) {
    super(root, ctx, "character-sheet");
  }

  protected async refresh(): Promise<void> {
    this.el.innerHTML = "<p>Loading character sheet…</p>";
    const result = await this.ctx.network.rest.get<CharacterStats>(`/api/v1/characters/${this.characterId}`);

    if (!result.ok) {
      this.el.innerHTML = `<p class="panel-error">${escapeHtml(result.message)}</p>`;
      return;
    }

    this.render(result.data);
  }

  private render(stats: CharacterStats): void {
    // Purely cosmetic: swaps the class-badge glyph shown next to the name
    // (art/icons/class-badges/*.svg) when the job matches one of the 4
    // named Tier-1 classes (1.4/1.5) — falls back to the base Wanderer
    // badge otherwise (Tier-2/3 classes aren't named in the spec yet, see
    // Team Atelier's PENDING_WORK.md item #4).
    this.el.className = `panel character-sheet job-${stats.jobId.toLowerCase().replace(/[^a-z0-9]/g, "")}`;

    const portraitUrl = PORTRAIT_BY_JOB[stats.jobId.toLowerCase()];
    const portraitHtml = portraitUrl
      ? `<img class="character-portrait" src="${portraitUrl}" alt="${escapeHtml(stats.jobId)} portrait" />`
      : "";

    this.el.innerHTML = `
      ${portraitHtml}
      <h3>${escapeHtml(stats.name)}</h3>
      <div class="stat-row"><span>Job</span><span>${escapeHtml(stats.jobId)} (Tier ${stats.jobTier})</span></div>
      <div class="stat-row"><span>Base Level</span><span>${stats.baseLevel}</span></div>
      <div class="stat-row"><span>Total XP</span><span>${stats.totalXp}</span></div>
      <div class="stat-row"><span>Skill Points</span><span>${stats.skillPoints}</span></div>
      <div class="stat-row"><span>Max HP</span><span>${stats.derivedStats.maxHp}</span></div>
      <div class="stat-row"><span>Max MP</span><span>${stats.derivedStats.maxMp}</span></div>
      <div class="stat-row"><span>Weapon ATK</span><span>${stats.derivedStats.weaponAtk}</span></div>
      <div class="stat-row"><span>Weapon MATK</span><span>${stats.derivedStats.weaponMatk}</span></div>
      <div class="stat-row"><span>DEF</span><span>${stats.derivedStats.def}</span></div>
      <div class="stat-row"><span>MDEF</span><span>${stats.derivedStats.matDef}</span></div>
      <div class="stat-row"><span>Attack Speed</span><span>${stats.derivedStats.attackSpeed.toFixed(2)}</span></div>
      <div class="stat-row"><span>STR / AGI / VIT</span><span>${stats.baseStats.str} / ${stats.baseStats.agi} / ${stats.baseStats.vit}</span></div>
      <div class="stat-row"><span>INT / DEX / LUK</span><span>${stats.baseStats.int} / ${stats.baseStats.dex} / ${stats.baseStats.luk}</span></div>
    `;
  }
}

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}
